/**
 * Mixpanel contract for `research_agents` (Issue Agent / trend detection).
 *
 * Event name is fixed; actions are string constants for type-safe call sites.
 */
import mixpanel from 'trackers/mixpanel'

export const RESEARCH_AGENTS_EVENT = 'research_agents' as const

/** Which top-level feed tab an issue card lives in. */
export type IssueFeedTab = 'feed' | 'tracked'

export const ResearchAgentsAction = {
  // ── Insights Feed ──────────────────────────────────────────────────────────
  insights_feed_view: 'insights_feed_view',
  feed_tab_change: 'feed_tab_change',
  feed_subsection_change: 'feed_subsection_change',
  feed_agent_filter_change: 'feed_agent_filter_change',
  feed_navigate_agent_center: 'feed_navigate_agent_center',
  jump_card_to_tracked: 'jump_card_to_tracked',
  jump_card_to_issues: 'jump_card_to_issues',
  jump_card_to_benchmarking: 'jump_card_to_benchmarking',
  // ── Emerging Issues tab ────────────────────────────────────────────────────
  emerging_issues_sort_change: 'emerging_issues_sort_change',
  emerging_issues_period_change: 'emerging_issues_period_change',
  emerging_issues_elevation_filter_change:
    'emerging_issues_elevation_filter_change',
  // ── Tracked tab ────────────────────────────────────────────────────────────
  tracked_sort_change: 'tracked_sort_change',
  // ── Agent config ───────────────────────────────────────────────────────────
  agent_list_view: 'agent_list_view',
  open_agent_config: 'open_agent_config',
  create_agent_form_open: 'create_agent_form_open',
  agent_type_select: 'agent_type_select',
  agent_create: 'agent_create',
  agent_create_cancel: 'agent_create_cancel',
  agent_edit_open: 'agent_edit_open',
  agent_edit_cancel: 'agent_edit_cancel',
  agent_edit_save: 'agent_edit_save',
  agent_run: 'agent_run',
  agent_generate_filters_click: 'agent_generate_filters_click',
  agent_email_preference_toggled: 'agent_email_preference_toggled',
  // ── Feedback ───────────────────────────────────────────────────────────────
  feedback_modal_open: 'feedback_modal_open',
  feedback_modal_dismiss: 'feedback_modal_dismiss',
  feedback_submit: 'feedback_submit',
  // ── Issue card actions ─────────────────────────────────────────────────────
  issue_card_click: 'issue_card_click',
  issue_track_click: 'issue_track_click',
  issue_untrack_click: 'issue_untrack_click',
  update_detail_click: 'update_detail_click',
  // ── Track-with-email opt-in modal ──────────────────────────────────────────
  track_email_prompt_shown: 'track_email_prompt_shown',
  track_email_prompt_answered: 'track_email_prompt_answered',
  // ── Benchmarking ───────────────────────────────────────────────────────────
  benchmarking_insight_click: 'benchmarking_insight_click',
  benchmarking_insight_view: 'benchmarking_insight_view',
  // ── Insight detail pages ───────────────────────────────────────────────────
  issue_detail_view: 'issue_detail_view',
  update_detail_view: 'update_detail_view',
  insight_share_click: 'insight_share_click',
  insight_chart_download: 'insight_chart_download',
  view_all_records_click: 'view_all_records_click',
  breakdown_dimension_change: 'breakdown_dimension_change',
} as const

export type ResearchAgentsActionType =
  (typeof ResearchAgentsAction)[keyof typeof ResearchAgentsAction]

export type OpenAgentConfigSource = 'issue_feed_card' | 'agent_list'

/** Default agent type for issue-detection agents (feed rows omit explicit type). */
export const ISSUE_DETECTION_AGENT_TYPE = 'issue_detection'

/** Default agent type for benchmarking-feed actions. */
export const BENCHMARKING_FEED_AGENT_TYPE = 'benchmarking'

export const BenchmarkingFeedAction = {
  AgentFilterChange: 'benchmarking_agent_filter_change',
  DirectionChange: 'benchmarking_direction_change',
  PeriodChange: 'benchmarking_period_change',
  PageChange: 'benchmarking_page_change',
} as const

export type BenchmarkingFeedActionType =
  (typeof BenchmarkingFeedAction)[keyof typeof BenchmarkingFeedAction]

export function trackResearchAgents(
  action: ResearchAgentsActionType,
  properties?: Record<string, unknown>,
): void {
  mixpanel.track(RESEARCH_AGENTS_EVENT, { action, ...properties })
}

export function trackBenchmarkingFeed(
  action: BenchmarkingFeedActionType,
  properties?: Record<string, unknown>,
): void {
  mixpanel.track(RESEARCH_AGENTS_EVENT, { action, ...properties })
}
